import { type Message } from "@/features/chat/types/sdk";
import { getBaseMessageId } from "./messageId";

/**
 * Deduplicate messages array by handling different message types appropriately.
 *
 * Two-Role Model Deduplication Strategy:
 * - Human messages: Deduplicated by exact ID match
 * - AI messages: Deduplicated by base ID (strips token suffix like "-token-0")
 *   - tool_use blocks within AI messages are deduplicated along with the message
 * - Other messages: Passed through without deduplication
 *
 * Note: In two-role model, tool_input and tool_result are not separate message types.
 * They are content blocks within AI (tool_use) and Human (tool_result) messages.
 *
 * @param messages - Array of messages to normalize
 * @returns Deduplicated array of messages
 */
export function normalizeMessages(messages?: Message[]): Message[] {
  if (!messages) return [];

  const normalized: Message[] = [];
  const aiIndexByBaseId = new Map<string, number>();
  const humanMessageIds = new Set<string>();

  for (const msg of messages) {
    // Deduplicate human messages by ID
    if (msg.type === "human" && msg.id) {
      if (humanMessageIds.has(msg.id)) {
        continue; // Skip duplicate human message
      }
      humanMessageIds.add(msg.id);
      normalized.push(msg);
      continue;
    }

    // Handle AI message deduplication for streaming tokens
    // In two-role model, tool_use blocks are within AI message content
    if (msg.type === "ai" && msg.id) {
      const baseId = getBaseMessageId(msg);
      if (!baseId) {
        normalized.push(msg);
        continue;
      }

      const existingIndex = aiIndexByBaseId.get(baseId);
      if (existingIndex === undefined) {
        aiIndexByBaseId.set(baseId, normalized.length);
        normalized.push(msg);
      } else {
        // Replace with latest version (includes updated tool_use blocks)
        normalized[existingIndex] = msg;
      }
      continue;
    }

    normalized.push(msg);
  }

  return normalized;
}
